// config.js - admin client configuration

const clientServerURL = process.env.REACT_APP_SERVER_URL;
const staticBaseURL = process.env.REACT_APP_STATIC_URL || '';

const config = {
  adminServer: {
    baseURL: clientServerURL,
    audioBaseURL: clientServerURL + '/api/audio/sample',
    retryLimit: 3,
    routes: {
      // auth
      signup: '/api/auth/signup',
      signin: '/api/auth/signin',
      logout: '/api/auth/logout',
      check: '/api/auth/check',
      // user
      profile: '/api/user/profile',
      profileEdit: '/api/user/profile/edit',
      userList: '/api/user/list',
      userDisable: '/api/user/disable',
      // role
      roleList: '/api/role/list',
      roleUpdate: '/api/role/update',
      // admin
      adminNewsList: '/api/admin/news/list',
      adminNewsCreate: '/api/admin/news/create',
      // audio
      audioUpload: '/api/audio/upload',
      audioInfo: '/api/audio/info',
      audioUpdate: '/api/audio/update',
      audioList: '/api/audio/list',
      audioTrash: '/api/audio/trash',
      // recipes
      recipeCreate: '/api/recipe/create',
      recipeInfo: '/api/recipe/info',
      recipeUpdate: '/api/recipe/update',
      recipeList: '/api/recipe/list',
      recipeTrash: '/api/recipe/trash',
    },
  },
  adminClient: {
    pages: {
      homepage: '/',
      signup: '/signup',
      signin: '/signin',
      profile: '/profile',
      profileEdit: '/profile/edit',
      userList: '/user/list',
      userDownload: '/user/download',
      userLookup: null, 
      userEdit: null,
      roleList: '/role/list',
      adminNews: '/admin/news',
      // note: adminNews permission is also used by backend to gate /api/admin/news routes
      audioUpload: '/audio/upload',
      audioBatch: '/audio/upload/batch',
      audioList: '/audio/list',
      audioView: '/audio/view',
      audioEdit: '/audio/edit',
      recipeCreate: '/recipe/create',
      recipeList: '/recipe/list',
      recipeView: '/recipe/view',
      recipeEdit: '/recipe/edit',
      notauth: '/notauth',
      error: '/error',
    },
  },
  mixEngine: {
    baseURL: 'https://driftconditions.org:8082',
    routes: {
      // queue
      queuePlaylist: '/api/queue/getplaylist',
      heartMix:      '/api/queue/heart',
    }
  },
  heart: {
    maxAgeHours:       48,  // localStorage TTL before hearted mix entry is pruned
    minFavoritesToKeep: 2,  // favorites count that exempts a mix from queue pruning
  },
  stream: {
    // url: 'https://driftconditions.org:8000/stream',
    url: 'https://usa14.fastcast4u.com/proxy/wmodes?mp=/1',
    restartTime: 5000,
    staleThreshold: 5000,  // ms since stop after which buffer is considered stale
  },
  sleepTimer: {
    fadeSeconds: 30,           // seconds over which audio fades out before stopping
    options: [15, 30, 60],     // available countdown durations in minutes
  },
  list: {
    recordsPerPage: 15,
  },
  profile: {
    topAudioCount: 5,
    recentPendingCount: 3,
  },
  wavesurfer: {
    container: '#waveform',
    waveColor: '#264d73',
    progressColor: '#68727a',
    cursorColor: 'red',
    cursorWidth: 2,
    dragToSeek: true,
    // height: 100,
    normalize: true,
    responsive: true,
    hideScrollbar: true,
    plugins: {
      regions: {
        dragSelection: {
          color: 'rgba(255, 0, 0, 0.1)',
        },
      },
    }
  },
  aceEditor: {
    useWorker: false, // Use the worker for syntax checking
    enableBasicAutocompletion: false,
    enableLiveAutocompletion: true,
    enableSnippets: false,
    highlightSelectedWord: true,
    minLines: 12,
    maxLines: 32,
    tabSize: 2,
    wrap: true,
  },
  audio: {
    // Validated by extension, not MIME type — see isAllowedFileType() in formatUtils.js
    allowedFileTypes: ['.mp3', '.wav', '.ogg', '.flac', '.aiff', '.aif', '.m4a'],
    classification: [
      'Ambient', 
      'Atmospheric', 
      'Environmental', 
      'Premixed', 
      'Soundscape', 
      'Archival', 
      'Spoken', 
      'Narrative', 
      'Instructional', 
      'VocalMusic', 
      'Instrumental', 
      'Experimental', 
      'Digital', 
      'Effect',
      'Other'
    ],
    fieldNotes: {
      classification: "A broad category that describes the broad type of audio. Pro-tip: Use as few classifications as applicable.",
      tags: "Describe the topic, theme, or texture of the audio, such as 'thunderstorm', 'lo fi', 'haunting', 'E#m key', or '100 bpm'. Use tags to highlight specific elements or moods in your audio. Separate tags with commas. Pro-tip: Use as many simple tags as you can think of.",
      filetypes: "Supported file types: mp3, wav, ogg, flac, aiff, m4a",
      status: "New uploads will be marked as 'Pending Review' until approved by a moderator.",
      copyright: "Please certify that this contains no copyrighted works for which you do not have a right to use.",
    },
    classificationFields: [
      {
        label: 'Ambient',
        value: 'ambient',
        moreInfo: "Music or sounds that form an ambient bed for other audio, e.g., drone, ambient music, longer static audio, minimalist music"
      },
      {
        label: 'Atmospheric',
        value: 'atmospheric',
        moreInfo: "Includes many field recordings, and longer found sounds, e.g., city ambiance, cafe recordings, crowd noise"
      },
      {
        label: 'Environmental',
        value: 'environmental',
        moreInfo: "Recordings of natural sounds, e.g., distant thunder, bird calls, rainfall, ocean waves"
      },
      {
        label: 'Premixed',
        value: 'premixed',
        moreInfo: "Already assembled mixes that would not benefit from additional mixing, e.g., radio show/podcast excerpts, mixed music tracks"
      },
      {
        label: 'Soundscape',
        value: 'soundscape',
        moreInfo: "Complex audio environments combining various sounds, e.g., urban soundscape, virtual environment audio, theatrical sound design"
      },
      {
        label: 'Archival',
        value: 'archival',
        moreInfo: "Historical recordings or sounds, e.g., old radio broadcasts, historical speeches, old TV shows"
      },
      {
        label: 'Spoken Word',
        value: 'spoken',
        moreInfo: "Spoken Word recordings, e.g., talks, poetry readings, speeches, interviews"
      },
      {
        label: 'Narrative',
        value: 'narrative',
        moreInfo: "Recordings that tell a story, e.g., radio dramas, storytelling sessions, narrative podcasts"
      },
      {
        label: 'Instructional',
        value: 'instructional',
        moreInfo: "Educational or instructional audio, e.g., language lessons, how-to guides, training programs"
      },
      {
        label: 'Vocal Music',
        value: 'vocalmusic',
        moreInfo: "Music that prominently features vocals, as opposed to instrumental"
      },
      {
        label: 'Instrumental',
        value: 'instrumental',
        moreInfo: "Music without vocals, e.g., classical music, instrumental jazz, ambient instrumental tracks"
      },
      {
        label: 'Experimental',
        value: 'experimental',
        moreInfo: "Non-traditional or avant-garde audio, e.g., noise music, sound experiments, unconventional audio compositions"
      },
      {
        label: 'Digital',
        value: 'digital',
        moreInfo: "Digital sounds and effects, e.g., synthesizer music, electronic beats, computer-generated sounds"
      },
      {
        label: 'Effect',
        value: 'effect',
        moreInfo: "Recordings that layer well over other audio, e.g., sound effects, stingers, radio signals, noise, transition sounds"
      },
      {
        label: 'Other',
        value: 'other',
        moreInfo: "Miscellaneous sounds that do not fit into other categories, e.g., unique or rare audio clips, uncategorized recordings"
      }
    ],
  },
  recipes: {
    classification: [
      'Ambient', 
      'Atmospheric', 
      'Environmental', 
      'Premixed', 
      'Soundscape', 
      'Archival', 
      'Spoken', 
      'Narrative', 
      'Instructional', 
      'VocalMusic', 
      'Instrumental', 
      'Experimental', 
      'Digital', 
      'Effect',
      'Other'
    ],
    fieldNotes: {
      tags: "These tags will be used when we search for clips, along with the tags in the recipe.",
      classification: "Select all the classifications that apply.",
      status: "New recipes will be marked as 'Pending Review' until approved by a moderator.",
    },
    
    // starting pattern for creating a new recipe (indentation matters here)
    example: `{
  // This is a basic RECIPE.
  //
  // Note that the system ignores any keys it doesn't recognize,
  // and comments like this are ignored as well 😀.
  // BUT the recipe has to be valid JSON-like code and the editor
  // will tell you if it's not.
  //
  tracks: [
    {
      // TRACKS are played simultaneously like a multi-track recording.
      //
      label: "my track",
      volume: 100,          // volume (0-100) of the track (optional)
      // EFFECTS modify the track
      //   structure: trim, first, shortest, longest
      //   looping:   loop, loop(n), crossfade, fadeout
      //   level:     norm, norm(voice), norm(spoken), norm(music), norm(bed)
      //   texture:   wave, wave(subtle), wave(subtle2), wave(inverseNoise),
      //              wave(subtleInverse), wave(transition), wave(noise2)
      //   color:     backward, faraway, faraway(vol), telephone
      //   routing:   duck(label)
      effects: [ ],
      clips: [
        {
          // CLIPS are audio files played one after another within a track.
          //
          // CLASS: ambient, atmospheric, environmental, premixed, soundscape,
          //        archival, spoken, narrative, instructional, vocalmusic,
          //        instrumental, experimental, digital, effect, other
          classification: [ 'vocalmusic', 'instrumental' ],
          // TAGS: describe the topic, theme, or texture of the audio. 
          // Use as many simple tags as you can think of.
          tags: [ "vintage", "jazz" ],
          // LENGTH: tiny(0-10s), short(10s-2m), medium(2-5m)
          //         long(5-10m), huge(10m+)
          length: [ 'short', 'medium' ],
          volume: 100,  // optional
          // EFFECTS: loop, crossfade, fadeout
          //   level: norm, norm(voice), norm(music), norm(bed)
          //   color: wave, backward, faraway, telephone, duck(label)
          effects: [ ],
        }
      ]
    },
  ]
}`,

    // starting pattern for adding a new track (indentation matters here)
    newTrack: `
    {
      label: "new TRACK",
      volume: 100,
      // EFFECTS: trim, first, shortest, longest, loop, crossfade, fadeout
      //   level: norm, norm(voice), norm(music), norm(bed)
      //   color: wave, backward, faraway, telephone, duck(label)
      effects: [],
      clips: [
        {
          // CLASS: ambient, atmospheric, environmental, premixed, soundscape,
          //        archival, spoken, narrative, instructional, vocalmusic,
          //        instrumental, experimental, digital, effect, other
          classification: [ "ambient" ],
          tags: [],
          // LENGTH: tiny(0-10s), short(10s-2m), medium(2-5m)
          //         long(5-10m), huge(10m+)
          length: [ "medium" ],
          effects: [],
        }
      ]
    },`,

    // starting pattern for adding a new clip (indentation matters here)
    newClip: `
        {
          // New CLIP
          classification: [ "ambient" ],
          tags: [],
          length: [ "medium" ],
          effects: [],
        },`,

    // starting pattern for adding a new silence clip (indentation matters here)
    newSilence: `
        {
          // SILENCE spacer
          classification: "silence",
          length: [ "tiny" ],
        },`,

  },
  recaptcha: {
    siteKey: '6LeGSaAsAAAAAC5vDSHIXZD291CnNXGTCfyrYF8b',
    scoreThreshold: 0.5,
  },
  app: {
    staticBaseURL,                                       // base URL for all static assets
    coverImageURLBase: staticBaseURL + '/img/covers',  // per-clip cover images (e.g. /img/covers/152.jpg)
    coverImageExt:     'jpg',
    altImageURLBase:   staticBaseURL + '/img/alt',     // fallback alt images  (e.g. /img/alt/coveralt-02.jpg)
  }

};

module.exports = config;